import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Send, Loader2, Calendar, AlertTriangle, ArrowLeft, FileText, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function LMSApply() {
  const navigate = useNavigate();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [projection, setProjection] = useState(null);
  const [form, setForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    if (form.leave_type_id && form.start_date && form.end_date) {
      fetchProjection();
    } else {
      setProjection(null);
    }
  }, [form.leave_type_id, form.start_date, form.end_date]);

  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get(`${API}/lms/leave-types`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLeaveTypes(res.data.leave_types || []);
    } catch {}
    setLoading(false);
  };

  const fetchProjection = async () => {
    try {
      const res = await axios.get(`${API}/lms/balance/projected`, {
        params: {
          leave_type_id: form.leave_type_id,
          start_date: form.start_date,
          end_date: form.end_date,
        },
        headers: getHeaders(), withCredentials: true,
      });
      setProjection(res.data);
    } catch {}
  };

  const calcDays = () => {
    if (!form.start_date || !form.end_date) return 0;
    const sd = new Date(form.start_date);
    const ed = new Date(form.end_date);
    if (sd > ed) return 0;
    let days = 0;
    const d = new Date(sd);
    while (d <= ed) {
      if (d.getDay() !== 0 && d.getDay() !== 6) days++;
      d.setDate(d.getDate() + 1);
    }
    return days;
  };

  const handleSubmit = async () => {
    if (!form.leave_type_id || !form.start_date || !form.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    const days = calcDays();
    if (days <= 0) {
      toast.error("Invalid date range");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/lms/apply`, {
        ...form,
        total_days: days,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success("Leave application submitted");
      navigate("/hr-hub/leave-management/my-leave");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    }
    setSubmitting(false);
  };

  const selectedType = leaveTypes.find((t) => t.id === form.leave_type_id);
  const totalDays = calcDays();

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>;
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate("/hr-hub/leave-management/dashboard")}
          className="h-7 w-7 p-0 rounded-none">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-lg font-semibold text-slate-800">Apply for Leave</h2>
      </div>

      <div className="bg-white border border-slate-200 p-5 space-y-4">
        {/* Leave Type */}
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Leave Type</label>
          <Select value={form.leave_type_id} onValueChange={(v) => setForm({ ...form, leave_type_id: v })}>
            <SelectTrigger className="h-9 text-xs rounded-none">
              <SelectValue placeholder="Select leave type" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {leaveTypes.filter((t) => !t.is_disabled).map((lt) => (
                <SelectItem key={lt.id} value={lt.id} className="text-xs">
                  {lt.name} ({lt.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedType?.requires_certificate && (
            <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" /> Medical certificate required
            </p>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Start Date</label>
            <Input type="date" value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="h-9 text-xs rounded-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">End Date</label>
            <Input type="date" value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="h-9 text-xs rounded-none" />
          </div>
        </div>

        {/* Working Days */}
        {totalDays > 0 && (
          <div className="p-3 bg-app border border-slate-200 text-xs text-slate-600">
            <span className="font-medium">{totalDays} working day{totalDays !== 1 ? "s" : ""}</span>
            {form.start_date && form.end_date && (
              <span> (excl. weekends & holidays)</span>
            )}
          </div>
        )}

        {/* Balance Projection */}
        {projection && (
          <div className={`p-3 border text-xs ${
            projection.is_sufficient
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}>
            <p className="font-medium mb-1">Balance Projection</p>
            <div className="space-y-0.5">
              <p>Current balance: {projection.current_balance} days</p>
              <p>Requested: -{projection.days_requested} days</p>
              {projection.accrual_before_start > 0 && (
                <p>Accrual before start: +{projection.accrual_before_start} days</p>
              )}
              <p className="font-semibold mt-1">
                Projected balance: {projection.projected_balance} days
                {!projection.is_sufficient && (
                  <span className="flex items-center gap-1 mt-1">
                    <AlertTriangle className="w-3 h-3" /> Insufficient balance
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Reason</label>
          <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
            rows={3} className="text-xs rounded-none" placeholder="Brief description of your leave..." />
        </div>

        {/* Submit */}
        <div className="flex items-center gap-2 pt-2">
          <Button onClick={handleSubmit} disabled={submitting || !form.leave_type_id || !form.start_date || !form.end_date}
            className="h-9 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500 gap-1.5">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Submit for Approval
          </Button>
          <Button variant="outline" onClick={() => navigate("/hr-hub/leave-management/dashboard")}
            className="h-9 text-xs rounded-none">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
